/**
 * UC-S2 Regional Invoiced Revenue Trends — filter/KPI logic ported from
 * ExecutiveDashboard.js `renderRegionalInvoicedRevenueWidget`.
 */

import type { QuarterKey } from './regionalClientPerformanceUtils'

export interface RegionalInvoicedTrendRow {
  calendar_year?: number
  calendar_month?: number
  invoice_month: string
  invoice_month_key?: number
  financial_year?: number
  financial_quarter?: number
  financial_year_period?: string
  geography: string
  region_currency?: string
  regional_invoiced_usd_net?: string | number
  regional_invoice_count?: string | number
  regional_unique_clients?: string | number
  mom_change_percent?: string | number
  yoy_change_percent?: string | number
  three_month_moving_avg?: string | number
  vat_treatment?: string
}

export function trendNum(v: string | number | undefined): number {
  if (v === undefined || v === null) return 0
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isFinite(n) ? n : 0
}

export function sortTrendRows(rows: RegionalInvoicedTrendRow[]) {
  return [...rows].sort((a, b) => {
    if (a.invoice_month_key != null && b.invoice_month_key != null) {
      return a.invoice_month_key - b.invoice_month_key
    }
    return (
      new Date(a.invoice_month).getTime() - new Date(b.invoice_month).getTime()
    )
  })
}

export function extractTrendGeographies(
  sorted: RegionalInvoicedTrendRow[]
): string[] {
  return [...new Set(sorted.map(r => r.geography).filter(Boolean))].sort()
}

/** Same FY / quarter / month / region rules as legacy UC-S2 widget. */
export function filterTrendRows(
  sorted: RegionalInvoicedTrendRow[],
  selectedYear: string,
  selectedQuarter: QuarterKey,
  selectedMonth: string,
  selectedRegion: string
): RegionalInvoicedTrendRow[] {
  return sorted.filter(item => {
    if (!item) return false

    let yearMatch = true
    if (selectedYear !== 'all') {
      yearMatch = false
      if (
        item.financial_year_period &&
        item.financial_year_period === selectedYear
      ) {
        yearMatch = true
      } else if (
        item.financial_year != null &&
        String(item.financial_year) === selectedYear
      ) {
        yearMatch = true
      } else if (
        item.calendar_year != null &&
        String(item.calendar_year) === selectedYear
      ) {
        yearMatch = true
      }
    }

    let quarterMatch = true
    if (selectedQuarter !== 'all') {
      const quarterNum = parseInt(selectedQuarter.replace('q', ''), 10)
      quarterMatch = item.financial_quarter === quarterNum
    }

    let monthMatch = true
    if (selectedQuarter === 'all' && selectedMonth !== 'all') {
      const cm =
        item.calendar_month ??
        new Date(item.invoice_month).getMonth() + 1
      monthMatch = cm === parseInt(selectedMonth, 10)
    }

    const regionMatch =
      selectedRegion === 'all' || item.geography === selectedRegion

    return yearMatch && quarterMatch && monthMatch && regionMatch
  })
}

export function groupTrendsByGeography(
  filtered: RegionalInvoicedTrendRow[]
): Record<string, RegionalInvoicedTrendRow[]> {
  const regionData: Record<string, RegionalInvoicedTrendRow[]> = {}
  for (const item of filtered) {
    const g = item.geography
    if (!regionData[g]) regionData[g] = []
    regionData[g].push(item)
  }
  return regionData
}

/**
 * Legacy `getKPIData`: sums revenue; MoM/YoY/3MA are simple averages across
 * rows (caller passes data already filtered by FY/Q/month/region).
 */
export function getTrendsKpiPayload(
  filteredData: RegionalInvoicedTrendRow[]
): {
  regional_invoiced_usd_net: number
  mom_change_percent: number
  yoy_change_percent: number
  three_month_moving_avg: number
} | null {
  if (filteredData.length === 0) return null

  const n = filteredData.length
  const totalRevenue = filteredData.reduce(
    (s, item) => s + trendNum(item.regional_invoiced_usd_net),
    0
  )
  const avgMom =
    filteredData.reduce(
      (s, item) => s + trendNum(item.mom_change_percent),
      0
    ) / n
  const avgYoy =
    filteredData.reduce(
      (s, item) => s + trendNum(item.yoy_change_percent),
      0
    ) / n
  const avgThree =
    filteredData.reduce(
      (s, item) => s + trendNum(item.three_month_moving_avg),
      0
    ) / n

  return {
    regional_invoiced_usd_net: totalRevenue,
    mom_change_percent: avgMom,
    yoy_change_percent: avgYoy,
    three_month_moving_avg: avgThree,
  }
}

export function regionDotClass(geography: string): string {
  switch (geography) {
    case 'North America':
      return 'bg-sky-500'
    case 'United Kingdom':
      return 'bg-emerald-500'
    case 'Australia':
      return 'bg-amber-500'
    case 'India':
      return 'bg-violet-500'
    case 'Other Regions':
      return 'bg-cyan-500'
    default:
      return 'bg-slate-400'
  }
}

export function vatSubline(vatTreatment: string | undefined): string {
  if (!vatTreatment) return ''
  if (vatTreatment.includes('VAT')) return 'VAT Excluded'
  return ''
}
